<script setup lang="ts">
import type { UnredeemedPackage } from "@/models/unredeemedPackage"
import { Button, ButtonLink, Card, Drawer, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { RedeemPackagesDrawerKey } from "@/symbols"

interface RedeemPackagesDrawerProps {
  packages?: UnredeemedPackage[]
  isFetching?: boolean
  canFetchMore?: boolean
}

withDefaults(defineProps<RedeemPackagesDrawerProps>(), {
  packages: undefined,
  isFetching: false,
  canFetchMore: false,
})

const emit = defineEmits<{
  (e: "fetchMore"): void
}>()

const { d, t } = useTranslation()
</script>

<template>
  <Drawer :name="RedeemPackagesDrawerKey" :title="t('redeem_packages_drawer.title')">
    <div class="flex flex-col gap-4">
      <p>{{ t('redeem_packages_drawer.message') }}</p>

      <div class="flex flex-col gap-2">
        <Card
          v-for="(event, index) in packages" :key="index" class="py-4 px-4 " variant="offwhite"
          :with-padding="false"
        >
          <div class="flex justify-between items-center">
            <div>
              <div class="font-semibold truncate max-w-[190px]  md:max-w-[280px]">
                {{ t('redeem_packages_drawer.package_name', { name: event.name }) }}
              </div>
              <div v-if="event.purchaseDate" class="text-label text-sm flex gap-1 items-center">
                <Icon icon="Calendar" />
                {{
                  t('redeem_packages_drawer.purchase_date', {
                    date: d(event.purchaseDate, "short"),
                  })
                }}
              </div>
            </div>
            <ButtonLink :href="event.link" class="py-1 px-2 mt-1">
              {{ t('redeem_packages_drawer.redeem') }}
            </ButtonLink>
          </div>
        </Card>
      </div>

      <div class="flex flex-col justify-center items-center gap-4">
        <div v-if="isFetching">
          <Icon icon="Loading" />
        </div>
        <Button v-if="canFetchMore" variant="secondary" :disabled="isFetching" @click="() => emit('fetchMore')">
          {{ t("load_more") }}
        </Button>
      </div>
    </div>
  </Drawer>
</template>
